import {PolymerElement} from '@polymer/polymer/polymer-element.js';
import '@polymer/paper-dialog/paper-dialog.js';
import '@polymer/paper-dialog-scrollable/paper-dialog-scrollable.js';
import {html} from '@polymer/polymer/lib/utils/html-tag.js';
import {Translations} from './translations.js';
import template from './ns-orders.html';
import {RunConfigService, TranslationsUtil, moment, storage, ConfigService, events} from '@nuskin/ns-util';
import {CartService, Agelocme} from '@nuskin/ns-shop';
import {UserService} from '@nuskin/ns-account';

import closeIcon from '../img/ic-close-small.svg';
import warningIcon from '../img/warning-icon.svg';

/**
 * Component displays last and next date of order.
 * Component converts millisecond version of last and next date into date format.
 * Labels and messages can be replaced with translated Strings.
 * Displays date of order process and the date your next order will be processed.
 *
 * <section>
 *  <h2>Content Guidelines</h2>
 *  <p>Here are some guidelines on how to properly use this element.</p>
 * </section>
 *
 * <h2>Orders Component Demo</h2>
 * <demo-snippet class="demoSnippet">
 *   <template>
 *     <ns-orders last-order="July 22" next-order="July 3" is-demo></ns-orders>
 *   </template>
 * </demo-snippet>
*/
export class NsOrders extends PolymerElement {
  static get is () {
    return 'ns-orders';
  }

  static get template () {
    let htmlElement = document.createElement('TEMPLATE');
    htmlElement.innerHTML = template;
    return htmlElement;
  }

  static get properties () {
    return {
      /**
      * hasTypeHeader
      * Used to display or not display the type header,
      */
      hasTypeHeader: {
        type: Boolean
      },
      /**
      * country
      * used for localization
      */
      country: {
        type: String
      },
      /**
      * language
      * used for localization
      */
      language: {
        type: String
      },
      /**
       * Object used to share data between ns-orders and ns-adp-wrapper
       */
      ordersDataObject: {
        type: Object,
        notify: true,
        observer: '_ordersDataObjectChanged'
      },
      /**
       * Object used to share shipping data with ns-adp-wrapper.
       */
      shippingDataObject: {
        type: Object,
        notify: true
      },
      /**
      * last order date
      */
      lastOrder: {
        type: String,
        value: '---'
      },
      /**
      * next order date
      */
      nextOrder: {
        type: String,
        value: '---'
      },
      /**
      * last order date in milliseconds
      */
      milliLastOrderDate: {
        type: Number
      },
      /**
      * next order date in milliseconds
      */
      milliNextOrderDate: {
        type: Number
      },
      /**
       * Disables override button and functionality if:
       *   Last Placed Date is equal to this month or
       *   the order has already been overridden or
       *   the order is held or
       *   today's date is within the blackout period or
       *   the adr is not editable
       */
      isNotOverridable: {
        type: Boolean,
        value: false
      },
      /**
       *
       */
      isNotHoldable: {
        type: Boolean,
        value: false
      },
      /**
       *
       */
      isNotDeletable: {
        type: Boolean,
        value: false
      },
      /**
       * Property to toggle ns-loader on wrapper
       */
      showLoader: {
        type: Boolean,
        notify: true
      },
      /**
       * Property to toggle modal overlay on wrapper (for popups)
       */
      hideOverlay: {
        type: Boolean,
        notify: true
      },
      /**
       * Array containing ADR hold options for dropdown
       * @private
       */
      holdsDropdownComputedOptions: {
        type: Array,
        value: []
      },
      selectedHoldCount: {
        type: Number
      },
      /**
       * Page upon which this component appears. Used for translations.
       */
      webPageName: {
        type: String,
        value: 'shop/adp-manage'
      },
      /**
       * Translations from the server. Triggers the translation assignment process.
       * If undefined and shouldAwaitTranslations=false, this component will fetch its own strings.
       */
      serverTranslations: {
        type: Object,
        observer: '_assignTranslations'
      },
      /**
       * If false, it will start the translation assignment process without waiting.
       * If true, it will wait for serverTranslations to be set before starting the translation assignment process.
       * If serverTranslations is defined already, the component will behave as if this property is true.
       */
      shouldAwaitTranslations: {
        type: Boolean,
        value: false
      },
      /**
       * Encoded value of the close icon used for passing this value to the template
       */
      closeIcon: {
        type: String,
        value: closeIcon
      },
      /**
       * Encoded value of the warning icon used for passing this value to the template
       */
      warningIcon: {
        type: String,
        value: warningIcon
      },
      /**
       * Determines whether we show our moth count
       */
      showMonthCount:{
        type: Boolean,
      },
      /**
       * Number of months ADR has shipped
       */
      monthCount: {
        type: Number,
        observer: '_monthCountChanged',
      },
      /**
       * Text that includes Number of months ADR has shipped
       */
      monthCountText: {
        type: String,
      },
      /**
       * Text that includes dates and warning for the override warning modal.
       */
      overrideDateWarningTextFilled: {
        type: String,
      },
      showMonthAsNumber: {
        type: Boolean
      },
      /**
       * holder for the feature flags enabled for the user 
       */
      featureFlags: {
        type: Object,
        value: {}
      }
    };
  }

  ready () {
    super.ready();
    if (!this.TRANSLATION_HELPER) this._localize();
    // CartService and UserService are made props so that functions using them can be tested
    this.cartService = CartService;
    this.userService = UserService;
    this.ConfigService = ConfigService;
    this.showProcessDateDisclaimer = ConfigService.getMarketConfig().adpManage.hasOwnProperty('showProcessDateDisclaimer') ?
      ConfigService.getMarketConfig().adpManage.showProcessDateDisclaimer : false;
    this.showMonthAsNumber = ConfigService.getMarketConfig().adpManage.hasOwnProperty('showMonthAsNumber') ?
      ConfigService.getMarketConfig().adpManage.showMonthAsNumber: false;

    this.set('showMonthCount', ConfigService.getMarketConfig().adpManage.showMonthCount);
  }

  /**
   * Sets language and country properties and prepares translations
   */
  _localize () {
    this.TRANSLATION_HELPER = new TranslationsUtil(this.language, this.country, Translations);
    this.language = this.TRANSLATION_HELPER.language;
    this.country = this.TRANSLATION_HELPER.country;
    if (!this.shouldAwaitTranslations) this._assignTranslations();
  }

  /**
   * Observer on serverTranslations that gathers all translations and maps them to properties.
   * Any strings set as properties and defined in the html attributes take precedence over translations from the server,
   * which in turn take precedence over the default translations in the translations file.
   */
  async _assignTranslations () {
    if (!this.TRANSLATION_HELPER) this._localize();
    this.serverTranslations = this.serverTranslations || await this.TRANSLATION_HELPER.getServerTranslations(this.webPageName);
    let allTranslations = Object.assign({}, this.TRANSLATION_HELPER.getDefaultTranslations(), this.serverTranslations);
    Object.entries(allTranslations).forEach(([key, value]) => {
      this[key] = this[key] || value;
    });
  }

  /**
   * Observer when ordersDataObject response is changed.
   */
  _ordersDataObjectChanged () {
    this.millilastOrderDate = this.ordersDataObject.lastPlacedDate;
    this.milliNextOrderDate = this.ordersDataObject.nextDeliveryDate;
    this.lastOrder = this._dateFormat(this.millilastOrderDate);
    this.nextOrder = this._dateFormat(this.milliNextOrderDate);
    this._initHoldsModal();
    this.set('monthCount', this.ordersDataObject.monthCount);
  }

  /**
   * Observer for filling out monthCountText
   */
  _monthCountChanged() {
    let text = this.monthCountLabel || 'Months fulfilled: {count}.';
    let textToDisplayed = text.replace('{count}', this.monthCount.toString());
    this.set('monthCountText', textToDisplayed);
  }

  _initHoldsModal() {
    const orderFrequency = this.shippingDataObject.frequency || 1;

    const rawOptions = (this.holdsDropdownOptions instanceof Array) ? this.holdsDropdownOptions : this.holdsDropdownOptions ? [this.holdsDropdownOptions] : [];

    const computedOptions = rawOptions.map(option => {
      const monthsSkipped = orderFrequency * (option.numSkipped || 0);
      const shipDate = new Date(this.milliNextOrderDate);
      shipDate.setMonth(shipDate.getMonth() + monthsSkipped);

      const label = (option.label || '').replace('{date}', this._dateFormat(shipDate.getTime()));

      return {
        numSkipped: option.numSkipped,
        label
      }
    });

    this.set('holdsDropdownComputedOptions', computedOptions);

    if (computedOptions.length > 0) {
      /*
      Force refresh of dropdown by selecting an invalid option,
      followed by selecting a valid default option.
      */
      this.set('selectedHoldCount', -1);
      setTimeout(() => {
        this.set('selectedHoldCount', computedOptions[0].numSkipped);
      }, 0);
    }

    this.showHoldsModalDeleteOption = !this.isNotDeletable && this.holdsModalDeleteAdrLabel;
    if (this.showHoldsModalDeleteOption) {
      // Perform split of deletion text in holds modal if available
      const startFragments = this.holdsModalDeleteAdrLabel.split('{del}');
      this.deleteAdrLabelStart = startFragments[0];
      if (startFragments.length > 1) {
        const endFragments = startFragments[1].split('{/del}');
        this.deleteAdrLabelAction = endFragments[0];
        this.deleteAdrLabelEnd = endFragments.length > 1 ? endFragments[1] : '';
      }
    }
  }

  _openAdrHoldsModal() {
    if (this.isNotHoldable) return;

    if (this.holdsDropdownComputedOptions.length > 0) {
      /*
      Force refresh of dropdown by selecting an invalid option,
      followed by selecting a valid default option.
      */
      this.set('selectedHoldCount', -1);
      setTimeout(() => {
        this.set('selectedHoldCount', this.holdsDropdownComputedOptions[0].numSkipped);
      }, 0);
    }

    this.shadowRoot.querySelector('#adrHoldsDialog').open();
    this.hideOverlay = false;
    this.toastState = '';

    events.publish(events.subscriptions.HOLDS_DIALOG_OPENED, {
      adr: this.ordersDataObject.orderId
    });
  }

  _closeAdrHoldsModal() {
    this.shadowRoot.querySelector('#adrHoldsDialog').close();
    this.hideOverlay = true;
    this.toastState = '';
  }

  _placeAdrHold() {
    if (this.isNotHoldable || this.selectedHoldCount === -1) {
      return;
    }

    /*
    Hold requests are made via a custom data field specifying the month/year
    of the shipment that is to be held. A request for a hold spanning multiple
    shipments is done by including one such field for each held shipment.
    The requested hold period is converted here to a set of corresponding
    hold request custom fields.
    */
    const customDataFields = [...Array(this.selectedHoldCount).keys()]
      .map(n => {
        const date = new Date(this.milliNextOrderDate);
        /*
        For bi-monthly ADRs, only months corresponding to normal shipment dates need to be included in the request.
        Therefore, the step count n is multiplied here by the ADR frequency (1 for monthly, 2 for bi-monthly).
        */
        date.setMonth(date.getMonth() + (n * (this.shippingDataObject.frequency || 1)));
        const monthYear = (date.getMonth() + 1).toString().padStart(2, '0') + '/' + date.getFullYear().toString();

        return {
          Key: 'ADR_HOLD',
          Value: monthYear,
          Type: ''
        };
      });

    this.shippingDataObject.body = null;
    this.shippingDataObject.options = {
      Custom: customDataFields,
      ShippingMethod: this.shippingDataObject.shipping.shipping.ShippingMethod.Code
    };

    this.set('shippingDataObject.updateType', 'Update');
    this._closeAdrHoldsModal();

    events.publish(events.subscriptions.HOLD_CONFIRMED, {
      adr: this.ordersDataObject.orderId,
      shipmentsHeld: this.selectedHoldCount
    });
  }

  _onClickDeleteAdr() {
    this._closeAdrHoldsModal();
    this.dispatchEvent(new CustomEvent('deleteAdr'));
  }

  _dateFormat (millisecondsDate) {
    if (this.showMonthAsNumber && this.featureFlags.cx14_1550_use_month_number) {
      return this._toMonthNumber(millisecondsDate)
    } else {
      return this._toMonthDay(millisecondsDate)
    }
  }

  /**
  * Converts date from milliseconds into correct format
  * param {*} millisecondsDate
  */
  _toMonthDay (millisecondsDate) {
    if (millisecondsDate) {
      let cfg = RunConfigService.getRunConfig();
      return moment(new Date(millisecondsDate)).locale(cfg.language + '_' + cfg.country).format('MMMM Do');
    } else {
      return '---';
    }
  }

  _toMonthNumber (millisecondsDate) {
    if (millisecondsDate) {
      let cfg = RunConfigService.getRunConfig();
      return moment(new Date(millisecondsDate)).locale(cfg.language + '_' + cfg.country).format('DD / MM / YYYY');
    } else {
      return '---';
    }
  }

  _findIfShouldShowOverrideDateModal() {
    let config = {
      enabled: Boolean(this.enableOverrideDateModal),
      requirePFC: Boolean(this.requirePFCOverrideDateModal),
      startDate: parseInt(this.blackListStartDateOverrideModal),
      endDate: parseInt(this.blackListEndDateOverrideModal),
    };

    if (!config.enabled || isNaN(config.startDate) || isNaN(config.endDate)) return false;

    let isPFC = this.userService.getUser().isPreferredCustomer();
    if (!isPFC && this.requirePFCOverrideDateModal) return false;

    let now = new Date();
    this._fillOverrideDateWarningText(now, config.startDate, config.endDate);
    let currentDay = now.getDate();
    return currentDay < config.startDate || config.endDate < currentDay;
  }

  _fillOverrideDateWarningText(date, start, end) {
    let dateStr = moment(new Date()).locale(this.language + '_' + this.country).format('LL');
    let text = this.overrideDateWarningModalBodyText.replace('{date}', dateStr)
        .replace('{start}', start)
        .replace('{end}', end);
    this.set('overrideDateWarningTextFilled', text)
  }

  _onOverrideOrder() {
    let shouldDisplayModal = this._findIfShouldShowOverrideDateModal();

    if (shouldDisplayModal) return this._toggleOverrideDateModal();
    return this._overrideOrder();

  }

  /**
   * Override functionality for ADR.
   */
  _overrideOrder () {
    this.showLoader = true;
    this.cartService.clearCart();
    const ORDER_REASON_ADR_OVERRIDE = 'ADR_OVERRIDE';
    storage.setItem(storage.metadata.ADR_OVERRIDE_ID, this.ordersDataObject.orderId, {});
    storage.setItem(storage.metadata.ORDER_REASON, ORDER_REASON_ADR_OVERRIDE, {});

    // For markets that do not keep the ADR items on override, go directly to the cart without further setup
    if (!nuskin.configuration.adpManage.keepCartOnAdrOverride) {
      CartService.goToCartPage();
    }

    const cartPromises = this.ordersDataObject.lineItems
      .filter(item => item.parentId === 0)
      .map(item => {
        let promise;

        const cartParams = {
          userId: this.userService.getUser().id,
          cntryCd: TranslationsUtil.country,
          language: TranslationsUtil.language,
          qty: item.requestedQuantity,
          redeem: item.payWithPoints
        };

        if (/^[0-9]{8}$/.test(item.sku)) {
          cartParams.sku = item.sku;
          promise = CartService.addSkuToCart(cartParams);
        } else {
          cartParams.agelocme = new Agelocme({
            code: findCustomValue(item.custom, 'ID', item.sku),
            label: findCustomValue(item.custom, 'LABEL', ''),
            name: findCustomValue(item.custom, 'NAME', '')
          });
          promise = CartService.addAgelocMeCodeToCart(cartParams);
        }

        return promise.catch(console.error);
      });

    Promise.all(cartPromises)
      .then(() => {
        CartService.goToCartPage();
      })
      .catch(console.error);

    /**
     * Finds and returns a custom data field value from a list of custom data fields.
     * @param {Object[]} customFields - List of custom data fields.
     * @param {string} key - Key to match.
     * @param {string} defaultValue - Value to return if key is not found.
     */
    function findCustomValue(customFields, key, defaultValue) {
      return (customFields || [])
        .filter(c => c.Key === key)
        .reduce((acc, c) => c.Value, defaultValue);
    }
  }

  /**
   * Display Modal warning about timing for VN
   * @param {*} e
   */
  _toggleOverrideDateModal() {
    this.$.overrideDateWarningModal.toggle();
  }
}
customElements.define(NsOrders.is, NsOrders);
